import axios from 'axios'
import { Message } from 'element-ui'
import router from '@/router'
import storage from "@/utils/storage";

// 全局设置 axios 带上 cookie
axios.defaults.withCredentials = true;
// 创建axios实例
const service = axios.create({
  baseURL: process.env.VUE_APP_API_URL, // 使用环境变量中设置的API URL
  timeout: 300000 // 请求超时时间
})

// 是否正在刷新token
let isRefreshing = false;
// 重试队列，每一项将是一个待执行的函数形式
let requests = [];

// 检查用户是否登录
const isUserLoggedIn = () => {
  const token = localStorage.getItem('token')
  const tokenExpires = localStorage.getItem('tokenExpires')

  if (!token || !tokenExpires) {
    return false
  }

  // 检查token是否过期
  if (Date.now() > parseInt(tokenExpires)) {
    // token过期，清除本地数据
    clearAuthData()
    return false
  }

  return true
}

// 清除认证数据
const clearAuthData = () => {
  localStorage.removeItem('token')
  localStorage.removeItem('user')
  localStorage.removeItem('tokenExpires')
  localStorage.removeItem('rememberMe')
  // 同时清除旧的存储方式
  storage.remove('accessToken')
  storage.remove('login-type')
}

// 跳转到登录页
const redirectToLogin = () => {
  const currentPath = router.currentRoute.path
  if (currentPath !== '/login') {
    Message.warning('登录已过期，请重新登录')
    router.push('/login')
  }
}

// 刷新token的方法
const refreshToken = () => {
  return service({
    url: '/api/auth/refresh',
    method: 'post'
  });
};

// request拦截器
service.interceptors.request.use(
    config => {
      // 在发送请求之前做些什么

      // 检查是否为登录相关的API，如果是则跳过登录检查
      const authApiPaths = ['/api/auth/login', '/api/auth/refresh']
      const isAuthApi = authApiPaths.some(path => config.url.includes(path))

      if (!isAuthApi) {
        // 检查用户是否登录
        if (!isUserLoggedIn()) {
          redirectToLogin()
          return Promise.reject(new Error('用户未登录'))
        }
      }

      // 添加token到请求头
      const token = localStorage.getItem('token') || storage.get('accessToken')
      if (token) {
        config.headers['Authorization'] = 'Bearer ' + token
      }

      // 保持旧的login-type逻辑兼容性
      const loginType = storage.get('login-type');
      if (loginType) {
        config.headers['login-type'] = loginType
      }

      // 确保URL格式正确，避免重复的http://等前缀
      if (config.url.startsWith('http://') || config.url.startsWith('https://')) {
        config.baseURL = '' // 如果URL已经是完整URL，则不使用baseURL
      }

      return config
    },
    error => {
      // 对请求错误做些什么
      console.error('request error: ', error)
      return Promise.reject(error)
    }
)

// response拦截器
service.interceptors.response.use(
    response => {
      const res = response.data
      return res
    },
    async error => {
      const { response } = error;
      const originalRequest = error.config;

      // 如果是401错误，且不是刷新token的请求
      if (response && response.status === 401 && !originalRequest._retry) {
        // 检查是否为登录API
        if (originalRequest.url.includes('/api/auth/login')) {
          // 登录API返回401，直接返回错误，不进行token刷新
          return Promise.reject(error);
        }

        // 检查是否为刷新token API
        if (originalRequest.url.includes('/api/auth/refresh')) {
          // 刷新token失败，清除认证数据并跳转登录
          clearAuthData();
          redirectToLogin();
          return Promise.reject(error);
        }

        if (isRefreshing) {
          // 如果正在刷新token，将请求放入队列
          return new Promise((resolve) => {
            requests.push(() => {
              resolve(service(originalRequest));
            });
          });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          const res = await refreshToken();
          if (res.code === 200) {
            // 刷新token成功，更新本地存储
            const { token, expiresIn } = res.data;
            localStorage.setItem('token', token);
            localStorage.setItem('tokenExpires', Date.now() + expiresIn * 1000);

            // 兼容旧的存储方式
            storage.set('accessToken', token);

            // 重新发送之前的请求
            originalRequest.headers['Authorization'] = 'Bearer ' + token;

            // 执行队列中的请求
            requests.forEach(callback => callback());
            requests = [];

            return service(originalRequest);
          } else {
            // 刷新token失败，清除认证数据并跳转到登录页
            clearAuthData();
            redirectToLogin();
            return Promise.reject(error);
          }
        } catch (refreshError) {
          // 刷新token失败，清除认证数据并跳转到登录页
          clearAuthData();
          redirectToLogin();
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // 其他错误处理
      if (response && response.status === 403) {
        Message({
          message: '权限不足，请联系管理员',
          type: 'error',
          duration: 5 * 1000
        });
      } else if (response && response.status >= 500) {
        Message({
          message: '服务器错误，请稍后重试',
          type: 'error',
          duration: 5 * 1000
        });
      } else {
        Message({
          message: error.message || '请求失败',
          type: 'error',
          duration: 5 * 1000
        });
      }

      return Promise.reject(error);
    }
)

/**
 * 辅助函数 - 构建路径参数URL
 * @param {string} url 基础URL
 * @param {object} pathParams 路径参数对象
 * @returns {string} 处理后的URL
 */
function buildUrlWithPathParams(url, pathParams) {
  if (!pathParams) return url

  let resultUrl = url
  Object.keys(pathParams).forEach(key => {
    // 替换 :paramName 或 {paramName} 形式的路径参数
    const pattern1 = new RegExp(`:${key}\\b`, 'g')
    const pattern2 = new RegExp(`\\{${key}\\}`, 'g')

    resultUrl = resultUrl
        .replace(pattern1, encodeURIComponent(pathParams[key]))
        .replace(pattern2, encodeURIComponent(pathParams[key]))
  })

  return resultUrl
}

// 封装GET请求
export function get(url, params, pathParams) {
  return service({
    url: buildUrlWithPathParams(url, pathParams),
    method: 'get',
    params
  })
}

// 封装POST请求 - JSON格式 (默认)
export function post(url, data, pathParams) {
  return service({
    url: buildUrlWithPathParams(url, pathParams),
    method: 'post',
    headers: {
      'Content-Type': 'application/json'
    },
    data
  })
}

// 封装POST请求 - Form Data格式
export function postFormData(url, data, pathParams) {
  // 如果data已经是FormData实例，直接使用；否则创建新的FormData
  const formData = data instanceof FormData ? data : new FormData()

  // 如果data不是FormData实例，需要将对象转换为FormData
  if (!(data instanceof FormData) && data) {
    Object.keys(data).forEach(key => {
      // 对于数组或对象类型，转换为JSON字符串
      if (data[key] !== null && data[key] !== undefined) {
        if (typeof data[key] === 'object' && !(data[key] instanceof File)) {
          formData.append(key, JSON.stringify(data[key]))
        } else {
          formData.append(key, data[key])
        }
      }
    })
  }

  return service({
    url: buildUrlWithPathParams(url, pathParams),
    method: 'post',
    // 使用FormData时，浏览器会自动设置正确的Content-Type和boundary
    // 不要手动设置Content-Type为multipart/form-data
    data: formData
  })
}

// 封装POST请求 - URL编码格式
export function postUrlEncoded(url, data, pathParams) {
  // 将数据转换为URL编码格式
  const urlEncodedData = new URLSearchParams()

  if (data) {
    Object.keys(data).forEach(key => {
      if (data[key] !== null && data[key] !== undefined) {
        urlEncodedData.append(key, data[key])
      }
    })
  }

  return service({
    url: buildUrlWithPathParams(url, pathParams),
    method: 'post',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded'
    },
    data: urlEncodedData
  })
}

// 封装PUT请求
export function put(url, data, pathParams) {
  return service({
    url: buildUrlWithPathParams(url, pathParams),
    method: 'put',
    data
  })
}

// 封装DELETE请求
export function del(url, params, pathParams) {
  return service({
    url: buildUrlWithPathParams(url, pathParams),
    method: 'delete',
    params
  })
}

// 导出登录状态检查函数，供其他组件使用
export { isUserLoggedIn, clearAuthData, redirectToLogin }

export default service 